import { FC, FormEvent, useEffect, useMemo, useState } from 'react';
import SearchableCountrySelect from './SearchableCountrySelect';
import { Country, fetchAllCountries } from '../api/countries';

interface AddCustomerModalProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (payload: { firstName: string; lastName: string; email: string; mobileNumber: string; dateOfBirth: string; countryCode?: string }) => Promise<void>;
}

const AddCustomerModal: FC<AddCustomerModalProps> = ({ open, onClose, onSubmit }) => {
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [mobileNumber, setMobileNumber] = useState('');
  const [countryCode, setCountryCode] = useState('60');
  const [dateOfBirth, setDateOfBirth] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [countries, setCountries] = useState<Country[]>([]);
  const [countriesLoading, setCountriesLoading] = useState(false);
  const [countriesError, setCountriesError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    setCountriesLoading(true);
    setCountriesError(null);
    fetchAllCountries()
      .then((data) => {
        if (!active) return;
        setCountries((data || []).filter((c) => Boolean(c.phoneCode)));
      })
      .catch((err) => {
        if (!active) return;
        const message = err instanceof Error ? err.message : 'Unable to load countries.';
        setCountriesError(message);
      })
      .finally(() => {
        if (active) setCountriesLoading(false);
      });
    return () => {
      active = false;
    };
  }, []);

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (saving) {
      return;
    }

    // Reset error state
    setError(null);

    // Basic validation
    const fn = firstName.trim();
    const ln = lastName.trim();
    const em = email.trim();
    const mob = mobileNumber.trim();
    const dob = dateOfBirth.trim();

    if (!fn) return setError('First name is required.');
    // Last name is optional
    if (!em || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(em)) return setError('Valid email is required.');
    const local = mob.replace(/\D/g, '');
    if (!/^\d{8,12}$/.test(local)) return setError('Phone must be 8–12 digits (excluding country code).');
    if (!dob) return setError('Date of birth is required.');

    setSaving(true);
    try {
      const cc = (countryCode || '60').replace(/\D/g, '');
      const full = `${cc}${local}`;
      await onSubmit({ firstName: fn, lastName: ln, email: em, mobileNumber: full, dateOfBirth: dob, countryCode: cc });

      setFirstName('');
      setLastName('');
      setEmail('');
      setMobileNumber('');
      setCountryCode('60');
      setDateOfBirth('');
      onClose();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unable to save customer.';
      setError(message);
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    if (saving) {
      return;
    }
    setError(null);
    setFirstName('');
    setLastName('');
    setEmail('');
    setMobileNumber('');
    setCountryCode('60');
    setDateOfBirth('');
    onClose();
  };

  if (!open) {
    return null;
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 px-4 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      aria-labelledby="add-customer-modal-title"
    >
      <div className="flex w-full max-h-[90vh] max-w-lg flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white text-slate-900 shadow-2xl">
        <div className="flex w-full items-center border-b border-slate-200 bg-slate-50 px-6 py-4">
          <h2 id="add-customer-modal-title" className="flex-1 text-lg font-semibold text-slate-900">
            Add customer
          </h2>
          <button
            type="button"
            className="ml-auto inline-flex h-8 w-8 items-center justify-center rounded-full border border-slate-300 text-slate-500 transition hover:bg-slate-100 hover:text-slate-700 focus:outline-none focus:ring-2 focus:ring-primary/40"
            onClick={handleClose}
            disabled={saving}
            aria-label="Close"
          >
            ✕
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4 px-6 py-6" noValidate>
          <label className="flex flex-col gap-1 text-sm font-medium text-slate-700">
            <span>First name <span className="text-red-500">*</span></span>
            <input
              type="text"
              value={firstName}
              onChange={(event) => setFirstName(event.target.value)}
              className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 placeholder:text-slate-400 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
              disabled={saving}
            />
          </label>

          <label className="flex flex-col gap-1 text-sm font-medium text-slate-700">
            <span>Last name</span>
            <input
              type="text"
              value={lastName}
              onChange={(event) => setLastName(event.target.value)}
              className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 placeholder:text-slate-400 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
              disabled={saving}
            />
          </label>

          <label className="flex flex-col gap-1 text-sm font-medium text-slate-700">
            <span>Email <span className="text-red-500">*</span></span>
            <input
              type="email"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 placeholder:text-slate-400 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
              disabled={saving}
            />
          </label>

          <div className="flex flex-col gap-1 text-sm font-medium text-slate-700">
            <span>Mobile number <span className="text-red-500">*</span></span>
            <div className="flex items-center gap-2">
              <SearchableCountrySelect
                countries={countries}
                value={countryCode}
                onChange={setCountryCode}
                disabled={saving}
                loading={countriesLoading}
                error={countriesError}
              />
              <input
                type="tel"
                value={mobileNumber}
                onChange={(event) => setMobileNumber(event.target.value)}
                placeholder="Local number"
                className="flex-1 rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 placeholder:text-slate-400 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
                disabled={saving}
              />
            </div>
            {countriesError ? (
              <p className="text-xs text-danger">{countriesError}</p>
            ) : null}
          </div>

          <label className="flex flex-col gap-1 text-sm font-medium text-slate-700">
            <span>Date of birth <span className="text-red-500">*</span></span>
            <input
              type="date"
              value={dateOfBirth}
              onChange={(event) => setDateOfBirth(event.target.value)}
              className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 placeholder:text-slate-400 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
              disabled={saving}
            />
          </label>

          {error ? <p className="text-sm text-danger">{error}</p> : null}

          <div className="flex items-center justify-end gap-3 pt-4">
            <button
              type="button"
              className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-medium text-slate-600 transition hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-primary/20"
              onClick={handleClose}
              disabled={saving}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-white shadow transition hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-primary/30"
              disabled={saving}
            >
              {saving ? 'Saving…' : 'Add customer'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddCustomerModal;
